import React, { useEffect, useState } from 'react';
import { getBasLabels } from '../../utils/commonUtils';
import AnimatedButton from '../atoms/AnimatedButton/AnimatedButton';
import './BookAServiceLanding.scss';
import Input from '../atoms/InputField/Input';
import { useDebounce } from '../../hooks/useDebounce';
import {
  getAutocompletePredictions,
  getUtilityLabels,
  getAllLabels,
} from '../../utils/commonUtils';
import Modal from '../Modal/Modal';
import SelectDistributorModal from '../ModalContentTypes/SelectDistributorModal/SelectDistributorModal';
import useAxios from '../../hooks/useAxios';
import { URL } from '../../constants/endpoints';
import Spinner from '../atoms/Spinner/Spinner';
import ShowSelectedDealerBAS from '../ShowSelectedDealerBAS/ShowSelectedDealerBAS';
import { useAppUiStore } from '../../hooks/useStores';
import { observer } from 'mobx-react';
let geocoder = new window.google.maps.Geocoder();

let autocompleteService = null;

function BookAServiceLanding() {
  const labels = getBasLabels();
  const { uiStore } = useAppUiStore();
  const [nameSearchValue, setNameSearchValue] = useState('');
  const [postalSearchValue, setPostalSearchValue] = useState('');
  const [dealersList, setDealersList] = useState([]);
  const [errorOnPostalSearch, setErrorOnPostalSearch] = useState(null);
  const [errorOnNameSearch, setErrorOnNameSearch] = useState(null);
  const [showDistributorModal, setShowDistributorModal] = useState(false);
  const [axiosConfigNameSearch, setAxiosConfigNameSearch] = useState(null);
  const [axiosConfigPostalSearch, setAxiosConfigPostalSearch] = useState(null);
  const [axiosConfigLocSearch, setAxiosConfigLocSearch] = useState(null);
  const [selectedPredictions, setSelectedPredictions] = useState('');
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [resetNameInput, setResetNameInput] = useState(false);
  const [resetPostalInput, setResetPostalInput] = useState(false);

  const {
    response: nameSearchRes,
    error: nameSearchErr,
    loading: nameSearchLoading,
    setShouldFetch: setShouldFetchNameSearch,
  } = useAxios(axiosConfigNameSearch, false);

  const {
    response: postalSearchRes,
    error: postalSearchErr,
    loading: postalSearchLoading,
    setShouldFetch: setShouldFetchPostalSearch,
  } = useAxios(axiosConfigPostalSearch, false);

  const {
    response: locSearchRes,
    error: locSearchErr,
    loading: locSearchLoading,
    setShouldFetch: setShouldFetchLocSearch,
  } = useAxios(axiosConfigLocSearch, false);

  //Handle name search response from API
  useEffect(() => {
    if (nameSearchRes) {
      if (
        nameSearchRes?.DealerList?.Dealers?.Dealer?.Name &&
        nameSearchRes?.DealerList?.Dealers?.Dealer?.HasServiceAddress === '1'
      ) {
        setDealersList([nameSearchRes.DealerList.Dealers.Dealer]);
        setShowDistributorModal(true);
        setShouldFetchNameSearch(false);
        setLoading(false);
        setResetNameInput(!resetNameInput);
        setErrorOnPostalSearch(null);
        setErrorOnNameSearch(null);
      } else if (
        nameSearchRes?.DealerList?.Dealers?.Dealer &&
        Array.isArray(nameSearchRes?.DealerList?.Dealers?.Dealer)
      ) {
        setDealersList(nameSearchRes.DealerList.Dealers.Dealer);
        setShowDistributorModal(true);
        setShouldFetchNameSearch(false);
        setLoading(false);
        setResetNameInput(!resetNameInput);
        setErrorOnPostalSearch(null);
        setErrorOnNameSearch(null);
      } else {
        setErrorOnNameSearch(labels.distributorNameError);
        setShouldFetchNameSearch(false);
        setLoading(false);
      }
    }

    if (nameSearchErr) {
      console.log(nameSearchErr);
      setLoading(false);
    }
  }, [nameSearchRes, nameSearchErr]);

  //Handle postal search response from API
  useEffect(() => {
    if (postalSearchRes) {
      if (postalSearchRes.DealerList.Dealers.Dealer?.Name) {
        setDealersList([postalSearchRes.DealerList.Dealers.Dealer]);
        setResetPostalInput(!resetPostalInput);
        setSelectedPredictions('');
        setPredictions([]);
        setErrorOnPostalSearch(null);
        setErrorOnNameSearch(null);
      } else {
        setDealersList(postalSearchRes.DealerList.Dealers.Dealer);
        setResetPostalInput(!resetPostalInput);
        setSelectedPredictions('');
        setPredictions([]);
        setErrorOnPostalSearch(null);
        setErrorOnNameSearch(null);
      }
      setShowDistributorModal(true);
      setShouldFetchPostalSearch(false);
      setLoading(false);
    }

    if (postalSearchErr) {
      console.log(postalSearchErr);
      setLoading(false);
    }
  }, [postalSearchRes, postalSearchErr]);

  //Handle location search response from API
  useEffect(() => {
    if (locSearchRes) {
      if (locSearchRes.DealerList.Dealers.Dealer?.Name) {
        setDealersList([locSearchRes.DealerList.Dealers.Dealer]);
      } else {
        setDealersList(locSearchRes.DealerList.Dealers.Dealer);
      }
      setShowDistributorModal(true);
      setShouldFetchLocSearch(false);
      setLoading(false);
    }

    if (locSearchErr) {
      console.log(locSearchErr);
      setLoading(false);
    }
  }, [locSearchRes, locSearchErr]);

  useEffect(() => {
    autocompleteService = new window.google.maps.places.AutocompleteService();
  }, []);

  const debouncedOnChange = useDebounce(getPredictions, 200);

  function searchDealerByName() {
    if (nameSearchValue && nameSearchValue !== '') {
      setAxiosConfigNameSearch({
        method: 'get',
        url: `${URL.DEALER_SEARCH_NAME}?dealershipname=${nameSearchValue}`,
      });
      setShouldFetchNameSearch(true);
      setLoading(true);
      setNameSearchValue('');
    } else {
      setErrorOnNameSearch(labels.distributorNameError);
    }
  }

  async function searchDealerByCode() {
    let validPostal = { isValid: false };
    let postalCode = selectedPredictions
      ? selectedPredictions.structured_formatting.main_text
      : postalSearchValue;
    if (selectedPredictions === '' && postalCode.length > 3) {
      validPostal = await validatePostalCode(postalCode.toUpperCase());
    }
    if (selectedPredictions === '' && !validPostal.isValid) {
      setErrorOnPostalSearch(labels.postalCodeError);
      return;
    } else {
      if (selectedPredictions === '') {
        postalCode = postalSearchValue.split(' ').join('');
      }
      setAxiosConfigPostalSearch({
        method: 'get',
        url: `${URL.DEALER_SEARCH_POSTAL}?pcode=${postalCode}`,
      });
      setShouldFetchPostalSearch(true);
      setLoading(true);
      setPostalSearchValue('');
    }
  }

  function validatePostalCode(postalCode) {
    var address = postalCode;
    let provinceName = 'default';
    let provinceCode = 'default';
    let postalCodeValue = 'default';

    return new Promise(function (resolve, reject) {
      try {
        geocoder.geocode(
          { address: address, componentRestrictions: { country: 'CA' } },
          function (results, status) {
            if (status === 'OK') {
              var formattedAddress;
              var isPostalCode = false;
              results.forEach((result) => {
                isPostalCode =
                  result.types.includes('postal_code') &&
                  !result.types.includes('postal_code_prefix');
                result.address_components.every(function (component) {
                  if (
                    component.types.includes('postal_code') &&
                    !component.types.includes('postal_code_prefix')
                  ) {
                    formattedAddress = component.long_name.replace(/ /g, '');
                    return false;
                  } else return true;
                });
              });
              if (isPostalCode && formattedAddress.includes(postalCode.replace(/ /g, ''))) {
                results[0].address_components.forEach(function (component, i) {
                  if (component.types.includes('administrative_area_level_1')) {
                    provinceName = component.long_name;
                    provinceCode = component.short_name;
                  }
                  if (component.types.includes('postal_code')) {
                    postalCodeValue = component.long_name.replace(/ /g, '');
                    console.log('postalCodeValue ', postalCodeValue);
                  } else {
                    postalCodeValue = postalCode.replace(/ /g, '');
                  }
                });
                resolve({
                  isValid: true,
                  provinceName,
                  provinceCode,
                  postalCodeValue,
                });
              } else {
                resolve({
                  isValid: false,
                  provinceName,
                  provinceCode,
                  postalCodeValue,
                });
              }
            } else {
              resolve({
                isValid: false,
                provinceName,
                provinceCode,
                postalCodeValue,
              });
            }
          }
        );
      } catch (err) {
        console.log(err);
      }
    });
  }

  // function searchDealerByLocation() {
  //   setLoading(true);
  //   if (navigator) {
  //     navigator.geolocation.getCurrentPosition((position) => {
  //       setAxiosConfigLocSearch({
  //         method: 'get',
  //         url: `${URL.DEALER_SEARCH_LOCATION}?pcode=null&latitude=${position.coords.latitude}&longitude=${position.coords.longitude}`,
  //       });
  //       setShouldFetchLocSearch(true);
  //       setLoading(true);
  //     });
  //   } else {
  //     setLoading(false);
  //   }
  // }

  function searchDealerByLocation() {
    setLoading(true); // Show the loader when starting the search

    if (navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // On success: user allowed location access
          setAxiosConfigLocSearch({
            method: 'get',
            url: `${URL.DEALER_SEARCH_LOCATION}?pcode=null&latitude=${position.coords.latitude}&longitude=${position.coords.longitude}`,
          });
          setShouldFetchLocSearch(true); // Trigger the data fetch
          setLoading(false); // Hide the loader once the location is successfully retrieved
        },
        (error) => {
          // On error: user denied location access or an error occurred
          console.error('Error occurred while retrieving location:', error);
          setLoading(false); // Hide the loader in case of error (like user denying access)
        }
      );
    } else {
      setLoading(false); // Hide the loader if geolocation is not supported
    }
  }

  function nameInputOnchange(val) {
    setNameSearchValue(val);
  }

  function postalInputOnchange(val) {
    setErrorOnPostalSearch('');
    if (selectedPredictions) {
      setSelectedPredictions('');
      setSearchValue('');
      return;
    }
    debouncedOnChange(val);
    setPostalSearchValue(val);
  }

  async function getPredictions(value) {
    if (!value) {
      setPredictions([]);
      setSelectedPredictions('');
      setSearchValue('');
      return;
    }
    try {
      const predictionsRes = (await getAutocompletePredictions(value, autocompleteService)) || [];
      setPredictions(predictionsRes);
    } catch (error) {
      setPredictions([]);
    }
  }

  function fillSelectedPrediction(selectedPrediction) {
    setSelectedPredictions(selectedPrediction);
    setSearchValue(selectedPrediction.description);
    setPostalSearchValue(selectedPrediction.description);
    setPredictions([]);
  }

  function showPredictionList() {
    return (
      <div className="dealer_prediction_container">
        <ul className="dealer_prediction_list">
          {predictions.map((prediction) => (
            <li
              key={prediction.place_id}
              tabIndex="0"
              className="predictions_list_item"
              onClick={() => fillSelectedPrediction(prediction)}
              onKeyDown={(e) => e.key === 'Enter' && fillSelectedPrediction(prediction)}>
              {prediction.structured_formatting.main_text}{' '}
              {prediction.structured_formatting.secondary_text}
            </li>
          ))}
        </ul>
        <span className="container_grow"></span>
      </div>
    );
  }

  return (
    <>
      {uiStore.dealerSelectionBAS ? (
        <ShowSelectedDealerBAS />
      ) : (
        <div className="book_service_component">
          <div className="title_container">
            <h2 className="title">{labels.title}</h2>
            <span className="subtitle">{labels.subTitle}</span>
          </div>
          <div className="bas_form_container">
            <div className="search_by_name">
              <span className="title">{labels.searchByNameTitle}</span>
              <span className="label">{labels.distributorNameLabel}</span>
              <div className="input_wrapper">
                <Input
                  type="text"
                  label={labels.distributorTextBoxWatermark}
                  error={errorOnNameSearch}
                  onChangeCallback={nameInputOnchange}
                  inputId="search_name_input"
                  resetField={resetNameInput}></Input>
                <AnimatedButton
                  text={labels.searchLabel}
                  theme="white"
                  clickHandler={searchDealerByName}></AnimatedButton>
              </div>
            </div>
            <div className="search_by_postal">
              <span className="title">{labels.byPostalCodeTitle}</span>
              <span className="label">{labels.postalCodeLabel}</span>
              <div className="input_wrapper">
                <Input
                  type="text"
                  label={labels.postalCodeTextWatermark}
                  error={errorOnPostalSearch}
                  onChangeCallback={postalInputOnchange}
                  inputId="search_postal_input"
                  inputSelected={searchValue}
                  resetField={resetPostalInput}>
                  {predictions.length ? showPredictionList() : null}
                </Input>
                <AnimatedButton
                  text={labels.searchLabel}
                  theme="white"
                  clickHandler={searchDealerByCode}></AnimatedButton>
                <span className="or_separator">{labels.orLabel}</span>
                <AnimatedButton
                  text={labels.useLocationLabel}
                  theme="white"
                  clickHandler={searchDealerByLocation}></AnimatedButton>
              </div>
            </div>
          </div>
          {loading ? (
            <Spinner theme="white" />
          ) : (
            <Modal
              hideCloseButton={false}
              showPopup={showDistributorModal}
              theme="carbon"
              version="expanded"
              togglePopupFn={() => setShowDistributorModal(!showDistributorModal)}>
              <SelectDistributorModal
                dealersList={dealersList}
                setShowDistributorModal={setShowDistributorModal}
              />
            </Modal>
          )}
        </div>
      )}
    </>
  );
}

export default observer(BookAServiceLanding);
